import React from 'react';
import { CheckCircle, Briefcase, Lightbulb, XCircle } from 'lucide-react';
import { useScreenWidth } from "../hooks/useWidth"
import { SMALL_WIDTH } from "../utils/constants"

export const Analytics = ({ analysis, setAnalysis, viewOnly = false }) => {
  const width = useScreenWidth()
  if (!analysis) return null;

  const {
    candidate_info,
    ats_analysis,
    strengths,
    actionable_improvements,
    target_roles
  } = analysis;

  const atsScore = ats_analysis?.score_out_of_100 || 0;

  const Section = ({ title, icon, children }) => (
    <div className="bg-white/10 p-6 max-sm:px-2 md:p-8 rounded-2xl shadow-lg mb-8 border border-blue-100">
      <div className="flex items-center mb-4">
        {icon && <div className="text-blue-600 mr-2">{icon}</div>}
        <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
      </div>
      <div className="border-t border-gray-200 pt-4">{children}</div>
    </div>
  );

  const ListGroup = ({ title, items, listIcon }) => {

    if (typeof (items) == 'string') {
      items = items.split(',')
    }

    return (
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-blue-800 mb-2">{title}</h3>
        <ul className="list-inside space-y-2 text-gray-700 pl-4 max-sm:pl-2">
          {items?.map((item, idx) => (
            <li key={idx} className="flex items-start leading-relaxed">
              <div className={`mr-3 mt-1 text-green-500 flex-shrink-0`}>
                {listIcon}
              </div>
              <span className='text-gray-600'>{item}</span>
            </li>
          ))}
        </ul>
      </div>);
  }

  const handleReset = () => {
    setAnalysis('')
  }
  return (
    <div className='bg-white/50'>

      {!viewOnly && <div className='text-right'>
        <button onClick={handleReset} className="text-indigo-600 hover:text-indigo-800 font-semibold">Analyze Another</button>
      </div>}
      <div className="z-10 px-6 sm:mx-28 max-sm:px-3 py-10  bg-white/10 backdrop-blur-sm rounded-xl border border-slate-400/30 shadow-lg">
        <div className='flex justify-between items-center'>
          <div className='flex flex-col items-start'>
            <h1 className="text-3xl font-extrabold text-center text-blue-800 max-sm:text-left max-sm:text-xl">
              Resume Analysis Report
            </h1>
            {candidate_info && (
              <div className="mt-2 text-gray-600">
                {candidate_info.name && <p className="font-semibold text-lg">{candidate_info.name}</p>}
                {candidate_info.email && <p className="text-sm">{candidate_info.email}</p>}
                {candidate_info.phone && <p className="text-sm">{candidate_info.phone}</p>}
              </div>
            )}
          </div>
          <div>
            <div className="relative w-24 h-24">
              <svg className="w-full h-full" viewBox="0 0 36 36">
                <path
                  className="text-gray-200"
                  stroke="currentColor"
                  strokeWidth={width < SMALL_WIDTH ? "2" : "3"}
                  fill="none"
                  d={`${width < SMALL_WIDTH ?
                    `M18 8
                              a 10 10 0 0 1 0 20
                              a 10 10 0 0 1 0 -20`
                    :
                    `M18 2.0845
                              a 15.9155 15.9155 0 0 1 0 31.831
                              a 15.9155 15.9155 0 0 1 0 -31.831`
                    }`}
                />
                <path
                  className={`${atsScore < 30 ? 'text-red-500' : 'text-green-500'}`}
                  stroke="currentColor"
                  strokeWidth={width < SMALL_WIDTH ? "2" : "3"}
                  fill="none"
                  // strokeDasharray={`${atsScore}, 100`}
                  strokeDasharray={width < SMALL_WIDTH ? `${(atsScore / 100) * 62.8}, 62.8` : `${atsScore}, 100`}
                  // 2*pi*radius = 62.8, radius = 10 for smaller screen
                  strokeLinecap="round"
                  d={`${width < SMALL_WIDTH ?
                    `M18 8
                            a 10 10 0 0 1 0 20
                            a 10 10 0 0 1 0 -20`
                    :
                    `M18 2.0845
                            a 15.9155 15.9155 0 0 1 0 31.831
                            a 15.9155 15.9155 0 0 1 0 -31.831`
                    }`}
                />
              </svg>
              <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-2xl font-bold max-sm:text-lg ${atsScore < 30 ? 'text-red-600' : 'text-green-600'}`}>
                {atsScore}%
              </div>
            </div>
            {
              width >= SMALL_WIDTH && <div className='text-gray-600 font-serif font-semibold max-sm:text-center mt-2'>
                ATS SCORE
              </div>
            }
          </div>
        </div>
        {/* TODO: ADD CLICK HANDLER */}

        <Section title="Strengths" icon={<CheckCircle className="w-6 h-6" />}>
          {strengths?.map((strength, idx) => (
            <ListGroup
              listIcon={<CheckCircle size={20} />}
              key={idx}
              title={strength.category
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase())}
              items={strength.points}
            />
          ))}
        </Section>

        {ats_analysis?.missing_keywords && ats_analysis.missing_keywords.length > 0 && (
          <Section title="Missing Keywords" icon={<XCircle className="w-6 h-6" />}>
            <div className="flex flex-wrap gap-2">
              {ats_analysis.missing_keywords.map((keyword, idx) => (
                <span key={idx} className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                  {keyword}
                </span>
              ))}
            </div>
          </Section>
        )}

        <Section title="Actionable Improvements" icon={<Lightbulb className="w-6 h-6" />}>
          <ListGroup
            listIcon={<Lightbulb size={20} color="blue" />}
            items={actionable_improvements?.map(imp => (
              <span key={imp.issue}>
                <strong className="text-gray-800">{imp.issue}:</strong> {imp.fix}
              </span>
            )) || []}
          />
        </Section>

        <Section title="Target Roles" icon={<Briefcase className="w-6 h-6" />}>
          <div className="grid sm:grid-cols-2 gap-4">
            {target_roles?.map((role, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-xl p-4 bg-white hover:shadow-md transition"
              >
                <h4 className="text-lg font-semibold text-gray-800">{role.title}</h4>
                <p className="text-sm text-gray-600 mt-2">{role.match_reason}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-4">
            *Based on the skills and experience identified by the AI.
          </p>
        </Section>
      </div>
    </div>
  );
};
